import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from 'date-fns';
import { AlertTriangle, Calendar, Users, Clock, Trash2 } from 'lucide-react';

interface DeleteSlotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  slotData: {
    id: string;
    title: string;
    start: Date | string;
    end?: Date | string;
    capacity: number;
    booked: number;
    price?: number;
  } | null;
  isLoading?: boolean;
}

export function DeleteSlotModal({ isOpen, onClose, onConfirm, slotData, isLoading = false }: DeleteSlotModalProps) {
  if (!slotData) return null;

  const startDate = new Date(slotData.start);
  const endDate = slotData.end ? new Date(slotData.end) : new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
  const bookingPercentage = (slotData.booked / slotData.capacity) * 100;

  const handleConfirm = async () => {
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      // Error is handled by parent component
      console.error('Delete confirmation error:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md border border-red-200/60 shadow-xl">
        <DialogHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-100 to-red-200 border border-red-200/50 flex items-center justify-center shadow-sm">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold text-slate-900">Delete Activity Slot</DialogTitle>
              <p className="text-sm text-slate-600 mt-1">This action cannot be undone</p>
            </div>
          </div>
        </DialogHeader>

        {/* Warning Message */}
        <div className="p-4 rounded-lg bg-red-50 border border-red-200/50">
          <div className="flex items-start gap-3">
            <Trash2 className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
            <div>
              <h4 className="font-semibold text-red-900 text-sm">Permanent Deletion</h4>
              <p className="text-red-800 text-sm mt-1">
                Are you sure you want to delete this activity slot? This will permanently remove the slot and cannot be recovered.
              </p>
              {slotData.booked > 0 && (
                <div className="mt-2 p-2 rounded bg-red-100 border border-red-200">
                  <p className="text-red-900 text-xs font-medium">
                    ⚠️ Warning: This slot has {slotData.booked} existing booking{slotData.booked > 1 ? 's' : ''}. 
                    Deleting will affect customer bookings.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Slot Details Card */}
        <Card className="border border-slate-200/60 shadow-sm bg-slate-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-slate-900 truncate flex-1 pr-2">{slotData.title}</h4>
              <Badge 
                variant="secondary" 
                className={`${
                  bookingPercentage >= 90 ? 'bg-red-100 text-red-800 border-red-200/50' :
                  bookingPercentage >= 70 ? 'bg-amber-100 text-amber-800 border-amber-200/50' :
                  bookingPercentage >= 30 ? 'bg-blue-100 text-blue-800 border-blue-200/50' :
                  'bg-emerald-100 text-emerald-800 border-emerald-200/50'
                }`}
              >
                {Math.round(bookingPercentage)}% Booked
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                <span className="text-slate-700">{format(startDate, 'MMM dd, yyyy')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                <span className="text-slate-700">
                  {format(startDate, 'HH:mm')} - {format(endDate, 'HH:mm')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-500" />
                <span className="text-slate-700">{slotData.booked}/{slotData.capacity} guests</span>
              </div>
              {slotData.price && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 text-xs">💰</span>
                  <span className="text-slate-700">${slotData.price}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-4 border-t border-slate-200/60">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1 border-slate-200/80 hover:bg-slate-50"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            className="flex-1 bg-red-600 hover:bg-red-700 shadow-md hover:shadow-lg transition-shadow"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Deleting...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Trash2 className="w-4 h-4" />
                Delete Slot
              </div>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 